import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/api/client";
import ShareButtons from "@/components/ShareButtons";
import { ArrowLeft, Clock, User, Globe, Loader2, ArrowRight, Sparkles, Eye } from "lucide-react";

// Upsert a <meta>/<link> tag in <head>, marked with data-seo="article" so
// we can replace them on slug change and remove them on unmount.
function _upsertHeadTag(tagName, attrs, { reuseExisting = false } = {}) {
  const key = tagName === "link" ? "rel" : attrs.property ? "property" : "name";
  let el;
  if (reuseExisting) {
    // Reuse the platform's existing tag (e.g. index.html's meta description) so we
    // don't end up with duplicate <meta name="description"> — Google reads the first.
    el = document.head.querySelector(`${tagName}[${key}="${attrs[key]}"]`);
  } else {
    el = document.head.querySelector(`${tagName}[data-seo="article"][${key}="${attrs[key]}"]`);
  }
  if (!el) {
    el = document.createElement(tagName);
    document.head.appendChild(el);
  }
  el.setAttribute("data-seo", "article");
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

function _stripHtml(html) {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return (div.textContent || "").replace(/\s+/g, " ").trim();
}

function applyArticleSeoMeta(article) {
  const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const canonical = `${window.location.origin}/read/${article.slug || article.id}`;
  const excerpt = _stripHtml(article.html).slice(0, 200);

  let image = "";
  if (article.hero_image_url) {
    image = article.hero_image_url.startsWith("http")
      ? article.hero_image_url
      : `${backend}${article.hero_image_url}`;
  } else {
    image = `${backend}/api/share/${article.id}/og.svg`;
  }

  // Standard meta — reuse index.html's existing description tag (no duplicates).
  _upsertHeadTag("meta", { name: "description", content: excerpt }, { reuseExisting: true });
  // Canonical (self-referencing, absolute)
  _upsertHeadTag("link", { rel: "canonical", href: canonical });
  // Open Graph
  _upsertHeadTag("meta", { property: "og:type", content: "article" });
  _upsertHeadTag("meta", { property: "og:title", content: article.title });
  _upsertHeadTag("meta", { property: "og:description", content: excerpt });
  _upsertHeadTag("meta", { property: "og:image", content: image });
  _upsertHeadTag("meta", { property: "og:url", content: canonical });
  // Twitter
  _upsertHeadTag("meta", { name: "twitter:card", content: "summary_large_image" });
  _upsertHeadTag("meta", { name: "twitter:title", content: article.title });
  _upsertHeadTag("meta", { name: "twitter:description", content: excerpt });
  _upsertHeadTag("meta", { name: "twitter:image", content: image });
}

const DEFAULT_DESCRIPTION = "AllBusinessOnline · The AI-first multi-business affiliate marketing platform";

function clearArticleSeoMeta() {
  document.head.querySelectorAll('[data-seo="article"]').forEach((el) => el.remove());
  // Only restore the platform default if no description tag survives — during
  // StrictMode's mount→cleanup→mount cycle the original index.html tag may still
  // be present, and appending a duplicate would leave two <meta name="description">.
  if (!document.head.querySelector('meta[name="description"]')) {
    const desc = document.createElement("meta");
    desc.setAttribute("name", "description");
    desc.setAttribute("content", DEFAULT_DESCRIPTION);
    document.head.appendChild(desc);
  }
}

export default function PublicRead() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/articles/${slug}`);
        setArticle(data);
        document.title = `${data.title} · Affiliate Terminal`;
        // Inject JSON-LD structured data for Google rich snippets.
        // Remove any previously-injected tags first so React 18 StrictMode double-mount
        // (or navigating between slugs) never leaves duplicate <script> in <head>.
        document.head.querySelectorAll('script[data-ld="article"]').forEach((el) => el.remove());
        [data.json_ld?.article, data.json_ld?.breadcrumb].filter(Boolean).forEach((obj) => {
          const el = document.createElement("script");
          el.type = "application/ld+json";
          el.setAttribute("data-ld", "article");
          el.textContent = JSON.stringify(obj);
          document.head.appendChild(el);
        });
        // Pre-render SEO head: meta description from excerpt, self-referencing
        // canonical, og:* and twitter:* cards.
        applyArticleSeoMeta(data);
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      document.title = "Affiliate Terminal";
      document.head.querySelectorAll('script[data-ld="article"]').forEach((el) => el.remove());
      clearArticleSeoMeta();
    };
  }, [slug]);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop;
      const height = doc.scrollHeight - doc.clientHeight;
      const pct = height > 0 ? Math.min(100, Math.max(0, (scrollTop / height) * 100)) : 0;
      setProgress(pct);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [article]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center text-neutral-500 flex items-center justify-center gap-3">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading article…
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <p className="font-serif text-2xl text-sky-100">Article not found.</p>
        <p className="text-neutral-500 mt-3">It may have been unpublished or removed.</p>
        <Link to="/browse" className="mt-6 inline-flex items-center gap-2 text-sky-300 hover:text-sky-100">
          <ArrowLeft className="w-4 h-4" /> Back to browse
        </Link>
      </div>
    );
  }

  const trackable = article.trackable_link
    ? (article.trackable_link.startsWith("http") ? article.trackable_link : `${process.env.REACT_APP_BACKEND_URL}${article.trackable_link}`)
    : null;

  return (
    <>
      {/* Reading progress bar (fixed, top of viewport) */}
      <div
        data-testid="reading-progress"
        className="fixed top-0 left-0 right-0 h-[3px] bg-slate-900 z-40"
        aria-hidden="true"
      >
        <div
          className="h-full bg-sky-400 transition-[width] duration-100 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
      <article className="max-w-3xl mx-auto px-6 py-16 fade-up">
      <button onClick={() => navigate(-1)} className="text-xs text-neutral-500 hover:text-sky-300 mb-8 flex items-center gap-1 font-mono uppercase tracking-widest" data-testid="back-btn">
        <ArrowLeft className="w-3 h-3" /> Back
      </button>

      {/* Header */}
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-4">
        — {article.network_key} · {article.niche}
      </p>
      <h1 data-testid="article-title" className="font-serif text-4xl lg:text-6xl leading-[1.1] text-sky-100 tracking-tight">
        {article.title}
      </h1>

      <div className="flex flex-wrap gap-6 mt-6 text-[11px] font-mono uppercase tracking-widest text-neutral-500">
        <Link
          to={`/author/${article.author_slug}`}
          data-testid="author-link"
          className="flex items-center gap-1.5 hover:text-sky-300 transition"
        >
          <User className="w-3 h-3" /> {article.author_name}
        </Link>
        <span className="flex items-center gap-1.5"><Globe className="w-3 h-3" /> {article.language || "English"}</span>
        <span className="flex items-center gap-1.5" data-testid="read-time"><Clock className="w-3 h-3" /> {article.read_time || 3} min read</span>
        <span className="flex items-center gap-1.5">SEO {article.seo_score}</span>
        {article.public_views > 0 && (
          <span className="flex items-center gap-1.5"><Eye className="w-3 h-3" /> {article.public_views} reads</span>
        )}
      </div>

      {/* Social share — top */}
      <div className="mt-6">
        <ShareButtons title={article.title} url={typeof window !== "undefined" ? window.location.href : ""} />
      </div>

      {article.hero_image_url && (
        <figure className="mt-8 mb-4">
          <img
            src={article.hero_image_url.startsWith("http") ? article.hero_image_url : `${process.env.REACT_APP_BACKEND_URL}${article.hero_image_url}`}
            alt={article.title}
            className="w-full rounded-lg border border-slate-800"
          />
          {article.hero_image_credit?.source === "unsplash" && article.hero_image_credit?.photographer && (
            <figcaption data-testid="hero-photo-credit" className="mt-2 text-xs text-slate-500">
              Photo by{" "}
              <a
                href={`${article.hero_image_credit.photographer_url}?utm_source=allbusinessonline&utm_medium=referral`}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-slate-300"
              >
                {article.hero_image_credit.photographer}
              </a>{" "}
              on{" "}
              <a
                href="https://unsplash.com?utm_source=allbusinessonline&utm_medium=referral"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-slate-300"
              >
                Unsplash
              </a>
            </figcaption>
          )}
        </figure>
      )}

      {/* Article body */}
      <div
        data-testid="article-body"
        className="article-html mt-10 text-lg leading-relaxed"
        dangerouslySetInnerHTML={{ __html: article.html }}
      />

      {/* Social share — bottom (enjoyed the read? share it) */}
      <div className="mt-12 pt-6 border-t border-slate-900">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-sky-400 mb-3">— Enjoyed this? Pass it on</p>
        <ShareButtons title={article.title} url={typeof window !== "undefined" ? window.location.href : ""} />
      </div>

      {/* Bottom CTA */}
      <div className="mt-16 pt-8 border-t border-slate-800">
        <div className="border border-sky-500/25 rounded-xl p-6 bg-gradient-to-br from-sky-500/8 to-transparent">
          <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400 mb-2">— Want to build your own?</p>
          <h3 className="font-serif text-2xl text-sky-100">
            Publish articles like this in <span className="italic text-sky-400">90 seconds.</span>
          </h3>
          <p className="text-neutral-400 mt-2 text-sm">The whole terminal — AI writer, PA-API sync, refresh radar, auto-tweets — free for the first 3 articles.</p>
          <Link
            to="/signup"
            data-testid="article-cta-signup"
            className="mt-4 inline-flex items-center gap-2 bg-sky-400 hover:bg-sky-300 text-slate-950 px-6 py-3 rounded-md font-medium"
          >
            <Sparkles className="w-4 h-4" /> Try for free
          </Link>
        </div>
      </div>

      {/* Related */}
      {article.related?.length > 0 && (
        <div className="mt-16">
          <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-4">Continue reading</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {article.related.map((r) => (
              <Link
                key={r.id}
                to={`/read/${r.slug || r.id}`}
                className="border border-slate-800 rounded-lg p-4 hover:border-sky-400/40 transition flex items-center justify-between gap-4 group"
              >
                <div className="min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400 mb-1 truncate">{r.niche}</p>
                  <h4 className="font-serif text-sky-100 group-hover:text-sky-300 line-clamp-2 leading-snug">{r.title}</h4>
                </div>
                <ArrowRight className="w-4 h-4 text-neutral-500 group-hover:text-sky-300 flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </article>
    </>
  );
}
